// Warranty claims list — /warranty (authenticated)
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { formatDate } from "@/lib/format/date";

interface Claim {
  id: string;
  code: string;
  serialNo: string;
  productName: string;
  variantSku: string;
  customerName?: string;
  resolutionType: string;
  status: string;
  receivedAt: string;
}

const STATUS_LABEL: Record<string, string> = {
  received: "Tiếp nhận",
  in_progress: "Đang xử lý",
  resolved: "Hoàn thành",
  cancelled: "Huỷ",
};

const RES_LABEL: Record<string, string> = {
  warranty: "Bảo hành",
  paid_repair: "Sửa có phí",
  rejected: "Từ chối",
};

const STATUS_COLOR: Record<string, string> = {
  received: "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  resolved: "bg-green-100 text-green-700",
  cancelled: "bg-gray-100 text-gray-500",
};

const RES_COLOR: Record<string, string> = {
  warranty: "bg-green-100 text-green-700",
  paid_repair: "bg-orange-100 text-orange-700",
  rejected: "bg-red-100 text-red-700",
};

function fmt(d: string) {
  return formatDate(d);
}

export function ClaimsListPage() {
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(0);
  const limit = 20;

  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  params.set("limit", String(limit));
  params.set("page", String(page));

  // Need both rows + meta.total → bypass apiGet and read the raw envelope.
  const { data, isLoading } = useQuery({
    queryKey: ["warranty-claims", status, from, to, page],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Claim[]; meta: { total: number } }>(
        `/v1/warranty/claims?${params.toString()}`,
      );
      return res.data;
    },
  });

  const claims = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const pages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Phiếu bảo hành</h1>
        <Link
          to="/warranty/new"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          + Tiếp nhận BH
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          className="rounded-md border bg-background px-3 py-2 text-sm"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
        >
          <option value="">Tất cả trạng thái</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input
          type="date"
          className="rounded-md border bg-background px-3 py-2 text-sm"
          value={from}
          onChange={(e) => { setFrom(e.target.value); setPage(0); }}
          placeholder="Từ ngày"
        />
        <input
          type="date"
          className="rounded-md border bg-background px-3 py-2 text-sm"
          value={to}
          onChange={(e) => { setTo(e.target.value); setPage(0); }}
          placeholder="Đến ngày"
        />
        {(status || from || to) && (
          <button
            className="rounded-md border px-3 py-2 text-sm text-muted-foreground"
            onClick={() => { setStatus(""); setFrom(""); setTo(""); setPage(0); }}
          >
            Xoá bộ lọc
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/50">
            <tr>
              {["Mã BH", "Serial", "Sản phẩm", "Khách hàng", "Loại", "Trạng thái", "Ngày tiếp nhận", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-medium text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Đang tải...</td></tr>
            )}
            {!isLoading && claims.length === 0 && (
              <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Không có phiếu bảo hành</td></tr>
            )}
            {claims.map((cl) => (
              <tr key={cl.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="px-4 py-3 font-mono font-medium">{cl.code}</td>
                <td className="px-4 py-3 font-mono text-xs">{cl.serialNo}</td>
                <td className="px-4 py-3">{cl.productName}</td>
                <td className="px-4 py-3 text-muted-foreground">{cl.customerName ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${RES_COLOR[cl.resolutionType] ?? ""}`}>
                    {RES_LABEL[cl.resolutionType] ?? cl.resolutionType}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[cl.status] ?? ""}`}>
                    {STATUS_LABEL[cl.status] ?? cl.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{fmt(cl.receivedAt)}</td>
                <td className="px-4 py-3">
                  <Link to={`/warranty/${cl.id}` as any} className="text-primary hover:underline text-xs">
                    Chi tiết
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center gap-2">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}
            className="rounded border px-3 py-1 text-sm disabled:opacity-40">‹ Trước</button>
          <span className="text-sm text-muted-foreground">Trang {page + 1}/{pages}</span>
          <button disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}
            className="rounded border px-3 py-1 text-sm disabled:opacity-40">Sau ›</button>
        </div>
      )}
    </div>
  );
}
